import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Info, Loader2, RefreshCw, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import type {
	CategoryResponse,
	EmailSettings,
	GmailLabel,
	GmailLabelRef,
} from "./settings-types";
import { CardSkeleton, SaveButton } from "./settings-ui";

// ============================================================
// Gmail Labels — Vorfilter für E-Mail-Import
// ============================================================

function FilterPipelineInfo() {
	return (
		<div className="mt-2 bg-blue-50/60 border border-blue-100 rounded-lg p-3 text-xs text-blue-800 space-y-1.5">
			<div className="flex items-start gap-2">
				<Info size={14} className="mt-0.5 shrink-0 text-blue-500" />
				<div className="space-y-1">
					<p className="font-medium">So werden E-Mails gefiltert:</p>
					<ol className="list-decimal ml-4 space-y-0.5 text-blue-700">
						<li>
							<strong>Gmail Labels</strong> (Vorfilter) — Nur E-Mails mit
							ausgewählten Labels werden importiert. Ca. 90% Trefferquote.
						</li>
						<li>
							<strong>Absender-Filter</strong> (excludeSenders, spamDomains) —
							Blockiert bekannte irrelevante Absender.
						</li>
						<li>
							<strong>Keyword-Classifier</strong> — Erkennt Buchungs-E-Mails
							anhand von Keywords im Betreff/Inhalt.
						</li>
					</ol>
					<p className="text-blue-600">
						Labels und Regeln ergänzen sich — sie überschreiben sich nicht.
					</p>
				</div>
			</div>
		</div>
	);
}

export function GmailLabelsCard() {
	const queryClient = useQueryClient();

	const settingsQuery = useQuery({
		queryKey: ["settings", "email"],
		queryFn: () =>
			api.get<CategoryResponse<EmailSettings>>("/settings/category/email"),
	});

	const [selectedLabels, setSelectedLabels] = useState<GmailLabelRef[]>([]);
	const [availableLabels, setAvailableLabels] = useState<GmailLabel[] | null>(
		null,
	);

	useEffect(() => {
		if (settingsQuery.data) {
			setSelectedLabels(settingsQuery.data.data.gmailLabels ?? []);
		}
	}, [settingsQuery.data]);

	const fetchLabelsMutation = useMutation({
		mutationFn: () =>
			api.get<{ data: GmailLabel[] }>("/settings/gmail/labels"),
		onSuccess: (res) => setAvailableLabels(res.data),
	});

	const saveMutation = useMutation({
		mutationFn: (labels: GmailLabelRef[]) =>
			api.put("/settings/category/email", { gmailLabels: labels }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["settings", "email"] });
		},
	});

	function toggleLabel(label: GmailLabel) {
		setSelectedLabels((prev) => {
			const exists = prev.some((l) => l.id === label.id);
			if (exists) return prev.filter((l) => l.id !== label.id);
			return [...prev, { id: label.id, name: label.name }];
		});
	}

	if (settingsQuery.isLoading) return <CardSkeleton />;

	return (
		<>
			<FilterPipelineInfo />
			<div className="space-y-3">
				<h3 className="font-medium text-stone-700 flex items-center gap-2">
					<Tag size={16} className="text-brand-600" />
					Schritt 1: Gmail Labels (Vorfilter)
				</h3>
				<div className="card space-y-4">
					<p className="text-xs text-stone-500">
						Nur E-Mails mit diesen Labels werden importiert. Ohne Auswahl werden
						alle Inbox-Mails verarbeitet.
					</p>

					{selectedLabels.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{selectedLabels.map((l) => (
								<span
									key={l.id}
									className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-700"
								>
									{l.name}
								</span>
							))}
						</div>
					)}
					{selectedLabels.length === 0 && (
						<p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
							Kein Label-Filter aktiv — alle Inbox-Mails werden verarbeitet
						</p>
					)}

					{!availableLabels ? (
						<button
							type="button"
							disabled={fetchLabelsMutation.isPending}
							onClick={() => fetchLabelsMutation.mutate()}
							className="btn-secondary text-xs !py-1.5 !px-3"
						>
							{fetchLabelsMutation.isPending ? (
								<Loader2 size={12} className="animate-spin inline mr-1" />
							) : (
								<RefreshCw size={12} className="inline mr-1" />
							)}
							Labels von Gmail laden
						</button>
					) : (
						<div className="space-y-2">
							<p className="text-xs font-medium text-stone-500">
								Verfügbare Labels ({availableLabels.length})
							</p>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
								{availableLabels.map((label) => (
									<label
										key={label.id}
										className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer hover:bg-stone-50 rounded px-2 py-1"
									>
										<input
											type="checkbox"
											checked={selectedLabels.some((l) => l.id === label.id)}
											onChange={() => toggleLabel(label)}
											className="rounded border-stone-300"
										/>
										{label.name}
									</label>
								))}
							</div>
							{availableLabels.length === 0 && (
								<p className="text-xs text-stone-400">
									Keine User-Labels im Gmail-Konto gefunden
								</p>
							)}
							<button
								type="button"
								onClick={() => {
									setAvailableLabels(null);
									fetchLabelsMutation.mutate();
								}}
								className="text-xs text-stone-400 hover:text-stone-600"
							>
								<RefreshCw size={10} className="inline mr-1" />
								Neu laden
							</button>
						</div>
					)}

					{fetchLabelsMutation.isError && (
						<p className="text-xs text-red-600 flex items-center gap-1">
							<AlertCircle size={12} />
							Fehler beim Laden der Labels
						</p>
					)}

					<SaveButton
						isPending={saveMutation.isPending}
						isSuccess={saveMutation.isSuccess}
						onClick={() => saveMutation.mutate(selectedLabels)}
					/>
				</div>
			</div>
		</>
	);
}
